import React from 'react';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import PageHeader from '@/components/PageHeader'
import { Card, Row, Col } from 'antd';
import { SelectC, InputC, RangePickerC, DatePickerC, Search, I2ProjectBt } from '@/i2antdV5'
import './Page.less'

import { fetchDBSummary } from '@/redux/dashboard/dbSummary/dbSummaryTypeSlice';
import { fetchCustomerSatisfaction } from '@/redux/dashboard/getCustomerSatisfaction/customerSatisfactionSlice';
import { fetchHomeServiceMonthlyList } from '@/redux/dashboard/getHomeServiceMonthlyList/homeServiceMonthlyListSlice';
import { fetchHomeServiceSummary } from '@/redux/dashboard/getHomeServiceSummary/homeServiceSummarySlice';
import { fetchProductSummary } from '@/redux/dashboard/getProductSummary/productSummarySlice';
import { fetchPackageSummary } from '@/redux/dashboard/getPackageSummary/packageSummarySlice';
import { fetchPackageMonthlyList } from '@/redux/dashboard/getPackageMonthlyList/packageMonthlyListSlice';
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice';
import { fetchStoreRoomStatus } from '@/redux/dashboard/getStoreRoomStatus/storeRoomStatusSlice';
import { fetchCommandCenterOccupiedRooms } from '@/redux/dashboard/commandCenterOccupiedRooms/commandCenterOccupiedRoomsSlice';
import { fetchCommandCenterStaffAgentsPage } from '@/redux/dashboard/commandCenterStaffAgentsPage/commandCenterStaffAgentsPageSlice';
import { fetchStaffList } from '@/redux/dashboard/staffList/StaffSlice';
import { fetchServiceCompletionRateSummary } from '@/redux/dashboard/getServiceCompletionRateSummary/serviceCompletionRateSummarySlice';

import { fetchOrdersByType } from '@/redux/order/maternityCenterOrderSlice'


import { fetchERPSalesReportHomeSummary } from '@/redux/erp/erpSalesReportHomeSummarySlice';
import { fetchERPgetStoreAnnualStatistics } from '@/redux/erp/erpgetStoreAnnualStatistics';
import { fetchERPgetStoreList } from '@/redux/erp/erpgetStoreList';
import { fetchERPStoreSalesTaget } from '@/redux/erp/erpStoreSalesTaget';
import { fetchERPStoreDataByDay } from '@/redux/erp/getERPStoreDataByDay';
import { fetchERPRoomList, fetchERPRoomListPost } from '@/redux/erp/erpRoomSlice';

import CalendarDashboard from './modal/CalendarDashboard';
import ServiceAlertsPanel from './modal/ServiceAlertsPanel';
import RevenueSummaryCard from './modal/RevenueSummaryCard';
import HeatMapVisualization from './modal/HeatMapVisualization';
import MaternityPackagesManagement from './modal/MaternityPackagesManagement';
import HomeServicePackages from './modal/HomeServicePackages';
import RoomsManagementModule from './modal/RoomsManagementModule';
import StaffManagementModule from './modal/StaffManagementModule';
import CompletionRateAnalytics from './modal/CompletionRateAnalytics';
import ServiceCompletion from './modal/SverviceCompletionAnalytics';
import CustomerSatisfactionAnalytics from './modal/CustomerSatisfactionAnalytics';
import { COMPLETION_RATE_CONFIG } from './config';
import i2Math from '@/utils/i2Math';

import QueryManager from '@/utils/queryManager';
import ApiPollingManager from '@/utils/ApiPollingManager';
import { formatERPDate } from '@/utils/queryConfig';

const breadcrumbList = [
  { breadcrumbName: '首页' },
  { breadcrumbName: 'dashboard' }
]

// 获取当天日期（格式：YYYY-MM-DD）
const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// 获取默认查询参数（每次调用都获取当天日期）
const getDefaultQuery = () => ({
  date: getTodayDate(), // 当天日期，格式：YYYY-MM-DD
  store_id: '16'
})

const defaultQuery = getDefaultQuery(); // 初始化时获取一次

/**
 * Col 布局配置管理
 * 统一管理页面中所有 Col 组件的响应式配置
 */
const COL_LAYOUT_CONFIG = {
  // 小卡片布局：第一行左侧两个小卡片
  smallCard: {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 6,
    xl: 6
  },
  // 大卡片布局：第一行右侧大卡片、第二行两个大卡片
  largeCard: {
    xs: 24,
    sm: 24,
    md: 24,
    lg: 12,
    xl: 12
  },
  // 套餐卡片布局：产康和到家套餐（带 className）
  packageCard: {
    xs: 24,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
    className: 'maternity-col'
  },
  // 四等分布局：第二行-b 的四个小卡片
  quarterCard: {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
    xl: 6
  },
  // 全宽布局：第三行的全宽卡片
  fullWidth: {
    xs: 24,
    sm: 24,
    md: 24,
    lg: 24,
    xl: 24
  }
}

/**
 * 接口配置管理
 * 类型说明：
 * - 'once': 只需加载一次，组件挂载时执行
 * - 'realtime': 实时获取，页面可见时持续轮询
 * - 'polling': 定时获取，按设定间隔轮询
 * - 'manual': 手动触发，不自动获取
 */
const getAPIConfig = (componentInstance) => ({
  // 只需加载一次的接口配置（组件挂载时执行）
  once: [
    // 示例：添加其他只需加载一次的接口
    // {
    //   name: 'fetchConfigData',
    //   dispatch: fetchConfigData,
    //   getParams: () => ({})
    // }
    {
      name: 'fetchStoreList',
      dispatch: fetchStoreList,
      getParams: () => ({
        serviceType: 'default',
        dataType: 'storeOption'
      })
    },
    {
      name: 'fetchERPgetStoreList',
      dispatch: fetchERPgetStoreList,
      getParams: () => ({})
    },
    {
      name: 'fetchStaffList',
      dispatch: fetchStaffList,
      getParams: (query) => ({ query: { ...query } })
    }
  ],
  
  // 实时获取接口配置（页面可见时持续轮询）
  realtime: [
    // {
    //   name: 'fetchDBSummary',
    //   dispatch: fetchDBSummary,
    //   interval: 30000, // 轮询间隔（毫秒），30秒，可根据需要调整
    //   getParams: (query) => ({ query: { ...query } })
    // },
    // {
    //   name: 'fetchProductSummary',
    //   dispatch: fetchProductSummary,
    //   interval: 30000, // 轮询间隔（毫秒），30秒，可根据需要调整
    //   getParams: (query) => ({ query: { ...query } })
    // }
  ],
  
  // 定时获取接口配置
  polling: [
    // 示例：定时获取接口
    // {
    //   name: 'fetchSomeData',
    //   dispatch: fetchSomeData,
    //   interval: 30000, // 30秒间隔（占位符，可自行设置）
    //   getParams: (query) => ({ query: { ...query } })
    // }
  ],
  
  // 手动触发接口（不自动获取）
  manual: [
    {
      name: 'fetchDBSummary', // 获取全局完成率
      dispatch: fetchDBSummary,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchProductSummary', //获取产康汇总数据
      dispatch: fetchProductSummary,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchCustomerSatisfaction', // 获取客户满意度
      dispatch: fetchCustomerSatisfaction,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchHomeServiceSummary', // 获取到家汇总数据
      dispatch: fetchHomeServiceSummary,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchHomeServiceMonthlyList', // 获取到家月度列表
      dispatch: fetchHomeServiceMonthlyList,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchPackageSummary', // 获取套餐汇总
      dispatch: fetchPackageSummary,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchPackageMonthlyList', // 获取套餐月度列表
      dispatch: fetchPackageMonthlyList,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchStoreRoomStatus', // 获取门店房间状态
      dispatch: fetchStoreRoomStatus,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchCommandCenterOccupiedRooms', // 开房数：order-total/list 在住 totalRow（checkInStatus=2）
      dispatch: fetchCommandCenterOccupiedRooms,
      getParams: (query) => {
        const rawId = query?.store_id;
        const hasStoreId =
          rawId !== undefined && rawId !== null && String(rawId).trim() !== '';
        const listQuery = {
          storeId: hasStoreId ? String(rawId).trim() : '',
          checkInStatus: 2,
          keyword: '',
          pageNumber: 1,
          pageSize: 20,
        };
        return { query: listQuery };
      },
    },
    {
      name: 'fetchCommandCenterStaffAgentsPage', // 员工总数：system/agents/page totalRow（state=1）
      dispatch: fetchCommandCenterStaffAgentsPage,
      getParams: (query) => {
        const rawId = query?.store_id;
        const hasStoreId =
          rawId !== undefined && rawId !== null && String(rawId).trim() !== '';
        const pageQuery = {
          storeId: hasStoreId ? String(rawId).trim() : '',
          state: '1',
          pageNumber: 1,
          pageSize: 20,
        };
        return { query: pageQuery };
      },
    },
    {
      name: 'fetchStaffList', // 获取员工列表
      dispatch: fetchStaffList,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchServiceCompletionRateSummary', // 获取日服务完成率汇总
      dispatch: fetchServiceCompletionRateSummary,
      getParams: (query) => ({ query: { ...query } })
    },
    {
      name: 'fetchERPSalesReportHomeSummary',
      dispatch: fetchERPSalesReportHomeSummary,
      getParams: (query) => {
        const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          userId: user.oa_id,
          companyId: user.com_id,
          date: formatERPDate(query.date, 'day'),
          depart: "sale"
        }
        return { query: formatQuery }
      }
    },
    {
      name: 'fetchERPStoreSalesTaget',
      dispatch: fetchERPStoreSalesTaget,
      getParams: (query) => {
        const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          userId: user.oa_id,
          companyId: user.com_id,
          month: formatERPDate(query.date, 'month'),
          depart: "sale"
        }
        return { query: formatQuery }
      }
    },
    {
      name: 'fetchERPSalesReportStoreAnnualSummary',
      dispatch: fetchERPgetStoreAnnualStatistics,
      getParams: (query) => {
        // 通过闭包访问组件实例
        const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          userId: user.oa_id,
          companyId: user.com_id,
          year: String(dayjs(query.date).year()),
          projectName: 'all'
        }
        const payload = { serviceType: 'all', query: formatQuery }
        return payload
      }
    },
    {
      name: 'fetchERPSalesReportStoreAnnualSummary',
      dispatch: fetchERPgetStoreAnnualStatistics,
      getParams: (query) => {
        // 通过闭包访问组件实例
        const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          userId: user.oa_id,
          companyId: user.com_id,
          year: String(dayjs(query.date).year()),
          projectName: '产后复原',
        }
        return { serviceType: 'postpartum', query: formatQuery }
      }
    },
    {
      name: 'fetchERPSalesReportStoreAnnualSummary',
      dispatch: fetchERPgetStoreAnnualStatistics,
      getParams: (query) => {
        // 通过闭包访问组件实例
        const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          userId: user.oa_id,
          companyId: user.com_id,
          year: String(dayjs(query.date).year()),
          projectName: '销售业绩'
        }
        return { serviceType: 'package', query: formatQuery }
      }
    },
    {
      name: 'fetchERPSalesReportStoreAnnualPrevYearPackage',
      dispatch: fetchERPgetStoreAnnualStatistics,
      getParams: (query) => {
        const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          userId: user.oa_id,
          companyId: user.com_id,
          year: String(dayjs(query.date).year() - 1),
          projectName: '销售业绩',
          writeMainSlot: false
        };
        return { serviceType: 'package', query: formatQuery };
      }
    },
    {
      name: 'fetchERPStoreDataByDay',
      dispatch: fetchERPStoreDataByDay,
      getParams: (query) => {
        // const user = componentInstance?.props?.app?.user ?? {};
        const formatQuery = {
          date: formatERPDate(query.date, 'day'),
        }
        return { query: formatQuery }
      }
    },
    // {
    //   name: 'fetchERPRoomList',
    //   dispatch: fetchERPRoomList,
    //   getParams: (query) => {
    //     const user = componentInstance?.props?.app?.user ?? {};
    //     const formatQuery = {
    //       // userId: user.oa_id,
    //       // companyId: user.com_id,
    //       store_id: query.store_id,
    //       date: formatERPDate(query.date, 'day'),
    //     }
    //     return { query: formatQuery }
    //   }
    // },
    // {
    //   name: 'fetchERPRoomListPost',
    //   dispatch: fetchERPRoomListPost,
    //   getParams: (query) => {
    //     const user = componentInstance?.props?.app?.user ?? {};
    //     const formatQuery = {
    //       // userId: user.oa_id,
    //       // companyId: user.com_id,
    //       // store_id: query.store_id,
    //       // date: formatERPDate(query.date, 'day'),
    //     }
    //     return { query: formatQuery }
    //   }
    // },
    // {
    //   name: 'fetchOrdersByType',
    //   dispatch: fetchOrdersByType,
    //   getParams: (query) => {
    //     const formatQuery = {
    //       ...query,
    //       pageNumber: 1,
    //       pageSize: 200
    //     }
    //    return {
    //     query: { ...formatQuery },
    //     orderType: 'maternity_center',
    //     // dataType: 'heatMap'
    //   }
    //   }
    // }
  ]
})

class CommandCenterPage extends React.Component {
  constructor(props) {
    super(props);
    this.lastUpdateTime = 0; // 上次更新时间，用于防抖
    this.lastUpdateKey = null; // 上次更新的key，用于去重
    
    // 缓存处理后的 erpStoreAnnualStatistics 数据
    this._cachedErpStoreData = null;
    this._cachedErpStoreDataKey = null;
    
    // 缓存处理后的 erpStoreDataByDay 数据
    this._cachedErpStoreDataByDay = null;
    this._cachedErpStoreDataByDayKey = null;
    
    // 缓存 enhancedProps，避免每次 render 都创建新对象
    this._enhancedProps = null;
    
    // 初始化QueryManager
    this.queryManager = new QueryManager(defaultQuery, {
      syncToUrl: false
    });
    
    // 创建 API 轮询管理器（传入组件实例以访问 Redux state）
    this.apiManager = new ApiPollingManager(getAPIConfig(this), this);
    
    // 设置更新回调（自动触发接口调用）
    this.queryManager.setUpdateCallback((query) => {
      this.apiManager.fetchData(query, true);
    });
    
    // 设置navigate函数（用于URL同步）
    props.navigate && this.queryManager.setNavigate(props.navigate);
  }

  // 将 store_list 数组转换为以 store_id 为 key 的对象
  convertStoreListToMap = (storeList) => {
    if (!Array.isArray(storeList)) {
      return {};
    }
    return storeList.reduce((acc, item) => {
      if (item && item.id !== undefined) {
        acc[String(item.id)] = item;
      }
      return acc;
    }, {});
  }

  // 获取处理后的 erpStoreAnnualStatistics 数据（带缓存）
  getProcessedErpStoreAnnualStatistics = () => {
    const { erpStoreAnnualStatisticsObj } = this.props;
    
    if (!erpStoreAnnualStatisticsObj) {
      return null;
    }

    // 基于 Redux 子树引用缓存；不能只用 store_list.length，否则金额变化但门店数不变时会拿到旧数据
    const cacheKey = erpStoreAnnualStatisticsObj;

    // 如果数据没有变化，返回缓存的结果
    if (this._cachedErpStoreData && this._cachedErpStoreDataKey === cacheKey) {
      return this._cachedErpStoreData;
    }

    // 处理数据转换：将 store_list 转换为 store_map，不保留 store_list
    const processServiceType = (serviceTypeData) => {
      if (!serviceTypeData) return null;
      const { store_list, ...rest } = serviceTypeData;
      return {
        ...rest,
        store_map: this.convertStoreListToMap(store_list)
      };
    };

    const processYearMap = (rawByYear, processor) => {
      if (!rawByYear || typeof rawByYear !== 'object') {
        return {};
      }
      return Object.keys(rawByYear).reduce((acc, y) => {
        acc[y] = processor(rawByYear[y]);
        return acc;
      }, {});
    };

    const processed = {
      ...erpStoreAnnualStatisticsObj,
      all: processServiceType(erpStoreAnnualStatisticsObj.all),
      postpartum: processServiceType(erpStoreAnnualStatisticsObj.postpartum),
      package: processServiceType(erpStoreAnnualStatisticsObj.package),
      allByYear: processYearMap(erpStoreAnnualStatisticsObj.allByYear, processServiceType),
      postpartumByYear: processYearMap(
        erpStoreAnnualStatisticsObj.postpartumByYear,
        processServiceType
      ),
      packageByYear: processYearMap(erpStoreAnnualStatisticsObj.packageByYear, processServiceType)
    };

    // 更新缓存
    this._cachedErpStoreData = processed;
    this._cachedErpStoreDataKey = cacheKey;

    return processed;
  }

  // 获取处理后的 erpStoreDataByDay 数据（转换为 store_map，带缓存）
  getProcessedErpStoreDataByDay = () => {
    const { erpStoreDataByDayObj } = this.props;
    
    if (!erpStoreDataByDayObj?.data?.list) {
      return erpStoreDataByDayObj;
    }

    // 生成缓存 key（基于数据的引用）
    const cacheKey = erpStoreDataByDayObj.data.list.length;

    // 如果数据没有变化，返回缓存的结果
    if (this._cachedErpStoreDataByDay && this._cachedErpStoreDataByDayKey === cacheKey) {
      return this._cachedErpStoreDataByDay;
    }

    // 转换为 store_map
    const store_map = this.convertStoreListToMap(erpStoreDataByDayObj.data.list);
    
    const processed = {
      ...erpStoreDataByDayObj,
      data: {
        ...erpStoreDataByDayObj.data,
        store_map
      }
    };

    // 更新缓存
    this._cachedErpStoreDataByDay = processed;
    this._cachedErpStoreDataByDayKey = cacheKey;

    return processed;
  }

  // 获取月份签单金额（根据 serviceType）：有 store_id 取单店当月；无 store_id 汇总全部门店当月
  getMonthRevenue = (serviceType) => {
    const processedErpStoreAnnualStatisticsObj = this.getProcessedErpStoreAnnualStatistics();
    const query = this.queryManager.getQuery();

    if (!query?.date || !processedErpStoreAnnualStatisticsObj) {
      return 0;
    }

    const serviceData = processedErpStoreAnnualStatisticsObj?.[serviceType];
    const storeMap = serviceData?.store_map;
    if (!storeMap || typeof storeMap !== 'object') {
      return 0;
    }

    const currentMonthNum = dayjs(query.date).month() + 1;
    const matchCurrentMonth = (item) => parseInt(String(item.month), 10) === currentMonthNum;

    const rawId = query?.store_id;
    const hasStoreId =
      rawId !== undefined && rawId !== null && String(rawId).trim() !== '';

    if (hasStoreId) {
      const storeObj = storeMap[String(rawId)];
      if (!storeObj?.list?.length) {
        return 0;
      }
      const monthData = storeObj.list.find(matchCurrentMonth);
      return monthData?.sum || 0;
    }

    let sum = 0;
    Object.values(storeMap).forEach((storeObj) => {
      if (!storeObj?.list?.length) return;
      const monthData = storeObj.list.find(matchCurrentMonth);
      sum = i2Math.i2Add(sum, Number(monthData?.sum) || 0);
    });
    return sum;
  }

  // 获取日维度门店对比数据：有 store_id 取单店；无 store_id 汇总全部门店（health / sales 等与接口字段一致）
  getStoreDataByDay = () => {
    const erpStoreDataByDayObj = this.getProcessedErpStoreDataByDay();
    const query = this.queryManager.getQuery();
    const storeMap = erpStoreDataByDayObj?.data?.store_map;

    if (!storeMap || typeof storeMap !== 'object') {
      return {};
    }

    const rawId = query?.store_id;
    const hasStoreId =
      rawId !== undefined && rawId !== null && String(rawId).trim() !== '';

    if (hasStoreId) {
      return storeMap[String(rawId)] || {};
    }

    let healthSum = 0;
    let salesSum = 0;
    Object.values(storeMap).forEach((row) => {
      if (!row || typeof row !== 'object') return;
      healthSum = i2Math.i2Add(healthSum, Number(row.health) || 0);
      salesSum = i2Math.i2Add(salesSum, Number(row.sales) || 0);
    });
    return { health: healthSum, sales: salesSum };
  }

  componentDidMount() {
    // 初始化query（从URL同步）
    // 更新defaultQuery中的date为当天日期（确保默认值是当天）
    const todayDate = getTodayDate();
    this.queryManager.defaultQuery = {
      ...this.queryManager.defaultQuery,
      date: todayDate
    };
    
    const initializedQuery = this.queryManager.initialize(location);
    
    // 如果初始化后的query中没有date（从URL没有获取到），则设置为当天日期
    if (!initializedQuery?.date) {
      initializedQuery.date = todayDate;
      this.queryManager.setQuery(initializedQuery, { autoFetch: false });
    }
    
    // 初始化 API 轮询管理器（会自动执行初始化、轮询、监听等操作）
    // 重写 fetchData 方法，使其使用 queryManager.getQuery() 获取当前查询参数
    const originalFetchData = this.apiManager.fetchData.bind(this.apiManager);
    this.apiManager.fetchData = async (value, showErrorDialog = true) => {
      // 如果传入了value，更新queryManager（但不自动触发接口调用，避免重复调用）
      if (value) {
        const oldCallback = this.queryManager.onUpdateCallback;
        this.queryManager.setUpdateCallback(null); // 临时禁用回调
        
        this.queryManager.updateQuery(value, undefined, { 
          autoFetch: false, // 不自动触发接口调用
          syncToUrl: this.queryManager.syncToUrl  // 使用配置的syncToUrl
        });
        
        this.queryManager.setUpdateCallback(oldCallback); // 恢复回调
      }
      
      // 获取当前query（优先使用 queryManager 的 query）
      const currentQuery = this.queryManager.getQuery();
      this.apiManager.currentQuery = currentQuery;
      
      // 调用原始的 fetchData 方法
      return originalFetchData(currentQuery, showErrorDialog);
    };
    
    // 重写 executeApiCall 方法，使其使用 queryManager.getQuery()
    const originalExecuteApiCall = this.apiManager.executeApiCall.bind(this.apiManager);
    this.apiManager.executeApiCall = async (config, showErrorDialog = true) => {
      // 更新 currentQuery 为 queryManager 的当前查询
      this.apiManager.currentQuery = this.queryManager.getQuery();
      return originalExecuteApiCall(config, showErrorDialog);
    };
    
    // 重写 handlePageVisible 方法，使其使用 queryManager.getQuery()
    const originalHandlePageVisible = this.apiManager.handlePageVisible.bind(this.apiManager);
    this.apiManager.handlePageVisible = () => {
      if (!this.apiManager.isPageVisible) {
        this.apiManager.isPageVisible = true;
        // 立即刷新数据
        const currentQuery = this.queryManager.getQuery();
        this.apiManager.currentQuery = currentQuery;
        this.apiManager.fetchData(currentQuery, false);
        // 重新启动轮询
        this.apiManager.startRealtimePolling();
        this.apiManager.startPolling();
      }
    };
    
    // 初始化管理器
    this.apiManager.currentQuery = initializedQuery;
    this.apiManager.init(initializedQuery);
  }

  componentDidUpdate(prevProps) {
    // 检查 Redux state 中的错误变化
    this.apiManager.checkReduxErrors(prevProps, (props) => {
      // 返回需要检查错误的 props 对象
      return {
        fetchDBSummary: props.dbSummaryObj,
        fetchProductSummary: props.productSummaryObj,
      };
    });

    // 如果 erpStoreAnnualStatisticsObj 数据变化，清除缓存
    if (prevProps.erpStoreAnnualStatisticsObj !== this.props.erpStoreAnnualStatisticsObj) {
      this._cachedErpStoreData = null;
      this._cachedErpStoreDataKey = null;
    }

    // 如果 erpStoreDataByDayObj 数据变化，清除缓存
    if (prevProps.erpStoreDataByDayObj !== this.props.erpStoreDataByDayObj) {
      this._cachedErpStoreDataByDay = null;
      this._cachedErpStoreDataByDayKey = null;
    }

    // 如果关键 props 变化，清除 enhancedProps 缓存
    if (prevProps.erpStoreAnnualStatisticsObj !== this.props.erpStoreAnnualStatisticsObj ||
        prevProps.erpStoreDataByDayObj !== this.props.erpStoreDataByDayObj ||
        prevProps.commandCenterOccupiedRoomsObj !== this.props.commandCenterOccupiedRoomsObj ||
        prevProps.commandCenterStaffAgentsPageObj !== this.props.commandCenterStaffAgentsPageObj) {
      this._enhancedProps = null;
    }
  }

  componentWillUnmount() {
    // 清理 API 轮询管理器
    this.apiManager.cleanup();
  }

  isObject = (value) => {
    return typeof value === 'object' && value !== null;
  }

  generateUpdateKey = (keyOrUpdates, value) => {
    if (typeof keyOrUpdates === 'string') {
      return `${keyOrUpdates}:${JSON.stringify(value)}`;
    }
    
    if (this.isObject(keyOrUpdates)) {
      const keys = Object.keys(keyOrUpdates).sort();
      return keys.map(k => `${k}:${JSON.stringify(keyOrUpdates[k])}`).join('|');
    }
    
    return null;
  }

  hasQueryChanged = (keyOrUpdates, value, currentQuery) => {
    if (this.isObject(keyOrUpdates)) {
      return Object.keys(keyOrUpdates).some(key => 
        currentQuery[key] !== keyOrUpdates[key]
      );
    }
    
    if (typeof keyOrUpdates === 'string') {
      return currentQuery[keyOrUpdates] !== value;
    }
    
    return true;
  }

  isDuplicateCall = (updateKey, now) => {
    return updateKey && 
           this.lastUpdateKey === updateKey && 
           now - this.lastUpdateTime < 300;
  }

  updateQuery = (keyOrUpdates, value, options = {}) => {
    const isActionCall = this.isObject(keyOrUpdates) && options.syncToUrl === undefined;
    
    const now = Date.now();
    const updateKey = this.generateUpdateKey(keyOrUpdates, value);
    
    if (this.isDuplicateCall(updateKey, now)) {
      return this.queryManager.getQuery();
    }
    
    this.lastUpdateTime = now;
    this.lastUpdateKey = updateKey;
    
    const currentQuery = this.queryManager.getQuery();
    
    if (!this.hasQueryChanged(keyOrUpdates, value, currentQuery)) {
      return currentQuery;
    }
    
    const finalOptions = {
      autoFetch: true,
      syncToUrl: options.syncToUrl ?? (isActionCall ? false : this.queryManager.syncToUrl),
      ...options
    };
    return this.queryManager.updateQuery(keyOrUpdates, value, finalOptions);
  }

  urlTypeFilterConfig = {
    serviceAlertsList: ['date']
  }

  isValidValue = (val) => {
    return val !== undefined && val !== null && val !== '';
  }

  buildQueryString = (options) => {
    const { query, urlType } = options;
    
    if (!this.isObject(query)) {
      return '';
    }
    
    const excludeFields = urlType ? (this.urlTypeFilterConfig[urlType] ?? []) : [];
    
    const searchParams = new URLSearchParams();
    Object.keys(query).forEach(key => {
      if (excludeFields.includes(key)) {
        return;
      }
      
      const val = query[key];
      if (this.isValidValue(val)) {
        searchParams.set(key, String(val));
      }
    });
    
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  fetchData = async (value, showErrorDialog = true) => {
    return this.apiManager.fetchData(value, showErrorDialog);
  }
  render() {
    const { storeObj, dispatch, dbSummaryObj } = this.props;
    const { default_storeOption: storeOption } = storeObj;
    // 使用QueryManager获取当前query
    let query = this.queryManager.getQuery();
    
    // 确保 store_id 是字符串类型，与 options.id 的类型保持一致
    if (this.isValidValue(query.store_id)) {
      query = {
        ...query,
        store_id: String(query.store_id)
      }
    }

    // 获取处理后的数据（带缓存）
    const processedErpStoreAnnualStatisticsObj = this.getProcessedErpStoreAnnualStatistics();
    const processedErpStoreDataByDayObj = this.getProcessedErpStoreDataByDay();
    
    // 计算月份收入（为不同 serviceType 计算）
    const monthRevenueData = {
      postpartum: this.getMonthRevenue('postpartum'), // 到家套餐使用
      package: this.getMonthRevenue('package'),       // 产康套餐使用
    };
    
    // 获取门店日数据
    const storeDataByDay = this.getStoreDataByDay();
    
    // 合并处理后的数据到 props（统一管理 queryManager 和处理后的数据）
    // 使用稳定的对象引用，避免每次 render 都创建新对象
    if (!this._enhancedProps || 
        this._enhancedProps.erpStoreAnnualStatisticsObj !== processedErpStoreAnnualStatisticsObj ||
        this._enhancedProps.erpStoreDataByDayObj !== processedErpStoreDataByDayObj ||
        this._enhancedProps.monthRevenueData?.postpartum !== monthRevenueData.postpartum ||
        this._enhancedProps.monthRevenueData?.package !== monthRevenueData.package ||
        this._enhancedProps.commandCenterOccupiedRoomsObj !== this.props.commandCenterOccupiedRoomsObj ||
        this._enhancedProps.commandCenterStaffAgentsPageObj !== this.props.commandCenterStaffAgentsPageObj) {
      this._enhancedProps = {
        ...this.props,
        erpStoreAnnualStatisticsObj: processedErpStoreAnnualStatisticsObj || this.props.erpStoreAnnualStatisticsObj,
        erpStoreDataByDayObj: processedErpStoreDataByDayObj || this.props.erpStoreDataByDayObj,
        queryManager: this.queryManager,
        // 预计算的数据
        monthRevenueData,
        storeDataByDay
      };
    }
    
    const enhancedProps = this._enhancedProps;
    console.log('enhancedProps', this.props);
    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
        //   extra={headerAction}
        />
        <div className='i2-container'>
          <Card 
            className="i2-card-page"
          >
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                {/* <SelectC label=''
                  searchKey="1"
                  // options={projectTeamOptions}
                /> */}
                <SelectC label=''
                  query={query}
                  searchKey='store_id'
                  options={storeOption} 
                  action={this.updateQuery}
                />  
                {/* 暂不开放 */}
                <DatePickerC label=''
                  query={query}
                  searchKey="date"
                  dateStrValue={true}
                  action={this.updateQuery}
                />
              </div>
            </section>
            <div className="command-center-page">
              <Row gutter={[6, 6]} className="row-first" >
                <Col {...COL_LAYOUT_CONFIG.smallCard}>
                  {/* 日历面板 */}
                  <CalendarDashboard/>
                </Col>
                <Col {...COL_LAYOUT_CONFIG.smallCard}>
                  <Link 
                    to={`/client/commandCenter/serviceAlertsList${this.buildQueryString({query: query, urlType: 'serviceAlertsList'})}`} 
                    // target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {/* 服务预警 */}
                    <ServiceAlertsPanel/>
                  </Link>
                </Col>
                <Col {...COL_LAYOUT_CONFIG.largeCard}>
                 {/* 门店总营收 */}
                  <RevenueSummaryCard  {...enhancedProps}/>
                </Col>
              </Row>
              <Row gutter={[6, 6]} className="row-second">
                <Col {...COL_LAYOUT_CONFIG.largeCard}>
                  {/* 热力地图 */}
                  <HeatMapVisualization/>
                </Col>
                <Col {...COL_LAYOUT_CONFIG.largeCard}>
                  <Row gutter={[0, 0]}>
                    <Col {...COL_LAYOUT_CONFIG.packageCard}>
                      {/* 产康套餐管理 */}
                      <MaternityPackagesManagement {...enhancedProps}/>
                    </Col>
                    <Col {...COL_LAYOUT_CONFIG.packageCard}>
                      {/* 到家套餐管理 */}
                      <HomeServicePackages {...enhancedProps}/>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row gutter={[6, 6]} className="row-second-b">
                <Col {...COL_LAYOUT_CONFIG.quarterCard}>
                  {/* 服务完成率 */}
                  <ServiceCompletion {...this.props}/>
                </Col>
                <Col {...COL_LAYOUT_CONFIG.quarterCard}>
                  {/* 客户满意度 */}
                  <CustomerSatisfactionAnalytics {...this.props}/>
                </Col>
                <Col {...COL_LAYOUT_CONFIG.quarterCard}>
                  {/* 房间管理 */}
                  <Link 
                    to={`/client/roomsManagement${this.buildQueryString({query: query, urlType: 'roomsManagement'})}`} 
                    rel="noopener noreferrer"
                  >
                    <RoomsManagementModule  {...enhancedProps}/>
                  </Link>
                </Col>
                <Col {...COL_LAYOUT_CONFIG.quarterCard}>
                  {/* 员工管理 */}
                  <Link 
                    to={`/client/staffList${this.buildQueryString({query: query, urlType: 'serviceAlertsList'})}`} 
                    rel="noopener noreferrer"
                  >
                  <StaffManagementModule  {...enhancedProps}/>
                  </Link>
                </Col>
              </Row>
              <Row gutter={[6, 6]} className="row-third">
                <Col {...COL_LAYOUT_CONFIG.fullWidth}>
                  {/* 全局完成率 */}
                  <CompletionRateAnalytics
                    {...enhancedProps}
                    completionRateConfig={COMPLETION_RATE_CONFIG}
                  />
                </Col>
              </Row>
            </div>
          </Card>
        </div>
      </div>
    );
  }
}

const CommandCenterPageWrapper = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 优化：使用单个 useSelector 获取整个 dashboard 状态，减少订阅数量
  const dashboardState = useSelector(state => state.dashboard ?? {});
  const erpState = useSelector(state => state.erp ?? {});
  const appState = useSelector(state => state.app ?? {});
  const orderState = useSelector(state => state.order?.order || {})

  // 直接从 Redux state 中提取需要的状态
  const props = {
    dispatch,
    location,
    navigate,
    app: appState,
    staffListObj: dashboardState.staffList ?? {},
    storeObj: dashboardState.storeList ?? {},
    dbSummaryObj: dashboardState.dbSummary ?? {},
    customerSatisfactionObj: dashboardState.customerSatisfaction ?? {},
    productSummaryObj: dashboardState.productSummary ?? {},
    homeServiceMonthlyListObj: dashboardState.homeServiceMonthlyList ?? {},
    homeServiceSummaryObj: dashboardState.homeServiceSummary ?? {},
    packageSummaryObj: dashboardState.packageSummary ?? {},
    packageMonthlyListObj: dashboardState.packageMonthlyList ?? {},
    storeRoomStatusObj: dashboardState.storeRoomStatus ?? {},
    commandCenterOccupiedRoomsObj: dashboardState.commandCenterOccupiedRooms ?? {},
    commandCenterStaffAgentsPageObj: dashboardState.commandCenterStaffAgentsPage ?? {},
    serviceCompletionRateSummaryObj: dashboardState.serviceCompletionRateSummary ?? {},
    erpSalesReportHomeSummaryObj: erpState.erpSalesReportHomeSummary ?? {},
    erpStoreAnnualStatisticsObj: erpState.erpgetStoreAnnualStatistics ?? {},
    erpStoreListObj: erpState.erpgetStoreList ?? {},
    erpStoreSalesTagetObj: erpState.erpStoreSalesTaget ?? {},
    erpStoreDataByDayObj: erpState.erpStoreDataByDay ?? {},
    erpRoomListObj: erpState.erpRoomList ?? {},
    orderObj: orderState.orderListPage ?? {},
  }
  
  return <CommandCenterPage {...props}/>;
}
export default CommandCenterPageWrapper;